/**
 * A2hsDemo — 안내 모달 안의 "미니 폰" 데모 3종. 모션은 a2hs.css 키프레임(JS 타이머 없음).
 *  · InstallDemoIos     : Safari 안 ERP 화면 → ≡ → 공유 → '홈 화면에 추가' → 상세 화면 '추가' → 바탕화면에 아이콘
 *  · InstallDemoAndroid : 버튼 → Chrome 설치 확인창 '설치' → 바탕화면에 아이콘 + 토스트
 *  · NotifyDemo         : 잠금화면에 실제 알림 문구(push_copy 기본 문구) 3장. 안드로이드는 '허용' 창부터.
 */
import { Icon } from "@/components/ui/Icon";
import { pushCopyDef, renderTemplate, sampleVars, type PushNotifyType } from "@/domain/push_copy";
import type { A2hsPlatform } from "@/domain/a2hs";

/** 홈 화면 아이콘 — index.html 의 apple-touch-icon 과 같은 파일 */
export const A2HS_APP_ICON = "/apple-touch-icon-v3.png?v=20261102";
const APP_NAME = "JG ERP";
const APP_HOST = "erp.jgind.co.kr";

function HamburgerSvg({ size = 11 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
      <path d="M5 8h14M5 12h14M5 16h10" />
    </svg>
  );
}
function ShareSvg() {
  return (
    <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v13M7 8l5-5 5 5" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}
function PlusBoxSvg() {
  return (
    <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}
function CheckSvg({ size = 10 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

/** 브라우저 안의 ERP 화면(대시보드 축소판) */
function MiniPage({ withUrlBar }: { withUrlBar: boolean }) {
  return (
    <div className="a2hs-page" aria-hidden="true">
      {withUrlBar ? (
        <div className="a2hs-page-url"><span>{APP_HOST}</span></div>
      ) : (
        <div className="a2hs-page-status" />
      )}
      <div className="a2hs-page-hd"><HamburgerSvg size={9} />{APP_NAME}</div>
      <div className="a2hs-page-bd">
        <b>오늘 출하</b>
        <div className="a2hs-page-cards"><span className="a2hs-page-card" style={{ height: 34 }} /><span className="a2hs-page-card" style={{ height: 34 }} /></div>
        <span className="a2hs-page-card" style={{ height: withUrlBar ? 56 : 64 }} />
        {!withUrlBar && <span className="a2hs-page-card" style={{ height: 40 }} />}
      </div>
    </div>
  );
}

/** 바탕화면 격자 — 마지막 칸에 우리 아이콘이 생긴다 */
function HomeGrid() {
  return (
    <div className="a2hs-grid" aria-hidden="true">
      <span /><span /><span /><span /><span /><span /><span />
      <span className="a2hs-grid-slot">
        <img className="a2hs-icon" src={A2HS_APP_ICON} alt="" />
        <span className="a2hs-check"><CheckSvg /></span>
      </span>
    </div>
  );
}

export function InstallDemoIos() {
  return (
    <div className="a2hs-demo-wrap">
      <div className="a2hs-phone a2hs-demo-ios" role="img" aria-label="아이폰 Safari 에서 메뉴 → 공유 → 홈 화면에 추가 → 추가 순서로 누르는 모습">
        <HomeGrid />
        <MiniPage withUrlBar={false} />

        {/* iOS 26 Safari 하단 바 */}
        <div className="a2hs-safari">
          <span className="a2hs-safari-dot" />
          <span className="a2hs-safari-url">
            <span className="a2hs-ham"><HamburgerSvg /><span className="a2hs-ring r1" /></span>
            <span>{APP_HOST}</span>
          </span>
          <span className="a2hs-safari-dot" />
        </div>

        {/* ① ≡ 메뉴 → 공유 */}
        <div className="a2hs-menu">
          <div className="a2hs-menu-row is-hot"><span>공유…</span><span><ShareSvg /><span className="a2hs-ring r2 is-strong" /></span></div>
          <div className="a2hs-menu-row"><span>책갈피 추가</span><span><svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round"><path d="M6 3h12v18l-6-4-6 4z" /></svg></span></div>
          <div className="a2hs-menu-row"><span>페이지에서 찾기</span><span><svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg></span></div>
        </div>

        {/* ② 공유 시트 → 홈 화면에 추가 */}
        <div className="a2hs-share">
          <div className="a2hs-share-hd">
            <img src={A2HS_APP_ICON} alt="" />
            <div><b>{APP_NAME}</b><small>{APP_HOST}</small></div>
          </div>
          <div className="a2hs-share-apps"><span /><span /><span /><span /></div>
          <div className="a2hs-share-list">
            <div className="a2hs-share-row"><span>복사</span><span><svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></svg></span></div>
            <div className="a2hs-share-row"><span>읽기 목록에 추가</span><span><HamburgerSvg size={10} /></span></div>
            <div className="a2hs-share-row is-hot"><span>홈 화면에 추가</span><span><PlusBoxSvg /><span className="a2hs-ring r3 is-strong" /></span></div>
          </div>
        </div>

        {/* ③ 상세 화면 → 추가 */}
        <div className="a2hs-detail">
          <div className="a2hs-detail-hd">
            <span className="a2hs-detail-x"><svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg></span>
            <span>홈 화면에 추가</span>
            <span className="a2hs-detail-add">추가<span className="a2hs-ring r4" /></span>
          </div>
          <div className="a2hs-detail-row">
            <img src={A2HS_APP_ICON} alt="" />
            <div className="a2hs-detail-fields">
              <div className="a2hs-detail-name">{APP_NAME}<span /></div>
              <div className="a2hs-detail-url">https://{APP_HOST}/</div>
            </div>
          </div>
          <div className="a2hs-detail-gap" />
          <div className="a2hs-detail-toggle"><span>웹 앱으로 열기</span><span className="a2hs-toggle" /></div>
          <div className="a2hs-detail-note">이 웹사이트에 빨리 접근할 수 있도록 홈 화면에 아이콘이 추가됩니다.</div>
        </div>
      </div>
    </div>
  );
}

export function InstallDemoAndroid() {
  return (
    <div className="a2hs-demo-wrap">
      <div className="a2hs-phone is-android a2hs-demo-android" role="img" aria-label="홈 화면에 추가 버튼을 누르면 설치 확인창이 뜨고 바탕화면에 아이콘이 생기는 모습">
        <HomeGrid />
        <MiniPage withUrlBar />

        <div className="a2hs-mini-btn">
          <span><svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg><span className="a2hs-ring r1" /></span>
          홈 화면에 추가
        </div>

        <div className="a2hs-sheet">
          <div className="a2hs-sheet-hd">
            <img src={A2HS_APP_ICON} alt="" />
            <div><b>앱 설치</b><small>{APP_NAME} · {APP_HOST}</small></div>
          </div>
          <div className="a2hs-sheet-ft">
            <span className="a2hs-sheet-cancel">취소</span>
            <span className="a2hs-sheet-install">설치<span className="a2hs-ring r2" style={{ left: "50%", top: "50%", width: 14, height: 14, margin: "-7px 0 0 -7px", inset: "auto" }} /></span>
          </div>
        </div>

        <div className="a2hs-toast"><CheckSvg size={12} />홈 화면에 {APP_NAME} 가 추가됐어요</div>
      </div>
    </div>
  );
}

/** 알림 데모에 보여 줄 종류 — 전 직원 공통으로 받는 출하 알림 3종(실제 기본 문구) */
const DEMO_TYPES: PushNotifyType[] = ["morning_plan", "ship_start", "ship_finish"];

export function NotifyDemo({ platform }: { platform: A2hsPlatform }) {
  const cards = DEMO_TYPES.map((t) => {
    const def = pushCopyDef(t);
    const vars = sampleVars(t);
    return { key: t, title: renderTemplate(def.defaultTitle, vars), body: renderTemplate(def.defaultBody, vars) };
  });
  const isIos = platform === "ios";
  return (
    <div className="a2hs-demo-wrap">
      <div className={`a2hs-phone is-notify a2hs-demo-notify ${isIos ? "is-ios" : "is-android"}`} role="img" aria-label="잠금화면에 출하 알림이 차례로 도착하는 모습">
        {isIos ? (
          <div className="a2hs-lock-glow" />
        ) : (
          <div className="a2hs-lock-status">
            <span>7:12 <img src={A2HS_APP_ICON} alt="" /></span>
            <span><Icon name="cloud" size={8} /></span>
          </div>
        )}
        <div className="a2hs-lock-time">7:12</div>
        <div className="a2hs-lock-date">11월 3일 화요일</div>
        <div className="a2hs-ntf-list">
          {cards.map((c) => (
            <div className="a2hs-ntf" key={c.key}>
              {isIos ? <img src={A2HS_APP_ICON} alt="" /> : <div className="a2hs-ntf-app"><img src={A2HS_APP_ICON} alt="" /> {APP_NAME} · 지금</div>}
              <div className="a2hs-ntf-text">
                <div className="a2hs-ntf-hd"><b>{c.title}</b>{isIos && <span>지금</span>}</div>
                <div className="a2hs-ntf-body">{c.body}</div>
              </div>
            </div>
          ))}
        </div>
        {!isIos && (
          <div className="a2hs-dlg">
            <img src={A2HS_APP_ICON} alt="" />
            <p>{APP_NAME}에서 알림을 보내도록<br />허용하시겠습니까?</p>
            <div className="a2hs-dlg-ft">
              <span>허용 안함</span>
              <span className="a2hs-dlg-allow">허용<span className="a2hs-ring" style={{ left: "50%", top: "50%", width: 14, height: 14, margin: "-7px 0 0 -7px", inset: "auto" }} /></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
